import styles from './testCaseModal.module.scss';
import {
  Trans,
  useTranslation
} from "react-i18next";
import { Input } from "../../shared/components/input/input";
import {
  useCallback,
  useEffect,
  useState
} from "react";
import {
  useFieldArray,
  useForm
} from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  array,
  number,
  object,
  string
} from "yup";
import { TestCase } from "../../types/testCaseTemplateTypes";
import { MessageWindow } from "../../shared/components/messageWindow/messageWindow";
import {
  useCreateTestCaseMutation,
  useDeleteTestCaseMutation,
  useUpdateTestCaseMutation
} from "../../api/requestService";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../store";
import { setUpdateModal } from "../../store/slices/mainSlice";
import { TestCaseTemplate } from "../../types/testCaseTemplateTypes";
import { ModalWindow } from "../../shared/components/modalWindow/modalWindow";
import { TestCaseModalParameterInput } from "./testCaseModalParameterInput/testCaseModalParameterInput";

type TestCaseModalProps = {
  onClose: (isSave: boolean, idCreate?: number) => void;
  testCaseTemplate: TestCaseTemplate,
  testCase?: TestCase
}

export const TestCaseModal = ({ onClose, testCaseTemplate, testCase }: TestCaseModalProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();

  const [showDeleteMessage, setShowDeleteMessage] = useState<boolean>();

  const [showConfirmCancel, setShowConfirmCancel] = useState(false);

  const [createTestCase, { data: createData, isSuccess: isSuccessCreateTestCase, isLoading: isLoadingCreate }] = useCreateTestCaseMutation();
  const [updateTestCase, { isSuccess: isSuccessUpdateTestCase, isLoading: isLoadingUpdate }] = useUpdateTestCaseMutation();
  const [deleteTestCase, { isSuccess: isSuccessDeleteTestCase, isLoading: isLoadingDelete }] = useDeleteTestCaseMutation();

  useEffect(() => {
    if (isSuccessCreateTestCase && !!createData) {
      dispatch(setUpdateModal({ show: true, isDelete: false }));
      onClose(true, createData);
    }
  }, [isSuccessCreateTestCase, createData]);

  useEffect(() => {
    if (isSuccessUpdateTestCase) {
      dispatch(setUpdateModal({ show: true, isDelete: false }));
      onClose(true);
    }
  }, [isSuccessUpdateTestCase]);

  useEffect(() => {
    if (isSuccessDeleteTestCase) {
      dispatch(setUpdateModal({ show: true, isDelete: true }));
      onClose(true);
    }
  }, [isSuccessDeleteTestCase]);


  const schema = object({
    name: string()
      .matches(/^[^"]*$/, t("common.notValid"))
      .typeError(t("common.notValid"))
      .required(t("common.notValid")),
    requestParameterValues: array().required().of(
      object().shape({
        id: number(),
        parameterId: number().required(),
        parameterName: string().required(),
        value: string()
      })
    ),
    responseParameterValues: array().required().of(
      object().shape({
        id: number(),
        parameterId: number().required(),
        parameterName: string().required(),
        value: string()
      })
    )
  })

  const {
    handleSubmit,
    control,
    formState: { errors, isDirty },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      name: testCase?.name || "",
      requestParameterValues: testCaseTemplate.requestParameters.map(parameter => {
        const requestItem = testCase?.requestParameterValues.find(item => item.parameterId === parameter.id);
        return {
          id: requestItem?.id,
          parameterName: parameter.name,
          parameterId: parameter.id,
          value: !!testCase ? requestItem?.value : parameter.value
        };
      }),
      responseParameterValues: testCaseTemplate.responseParameters.map(parameter => {
        const responseItem = testCase?.responseParameterValues.find(item => item.parameterId === parameter.id);
        return {
          id: responseItem?.id,
          parameterName: parameter.name,
          parameterId: parameter.id,
          value: !!testCase ? responseItem?.value : parameter.value
        };
      }),
    },
    mode: "onChange"
  });

  const {
    fields: requestParametersFields,
  } = useFieldArray({control, name: "requestParameterValues"});

  const {
    fields: responseParametersFields,
  } = useFieldArray({control, name: "responseParameterValues"});

  const onCancel = useCallback(() => {
    if (isDirty) {
      setShowConfirmCancel(true);
    } else {
      onClose(false);
    }
  }, [isDirty, onClose]);

  const closeConfirmCancelModal = useCallback((isConfirm: boolean) => {
    if (isConfirm) {
      onClose(false);
    }
    setShowConfirmCancel(false);
  }, [setShowConfirmCancel]);

  const onClickSave = (): void => {
    handleSubmit((value) => {
      const newTestCase: TestCase = {
        name: value.name,
        testRailTestCaseId: !!testCase?.id ? testCase?.testRailTestCaseId : undefined,
        testCaseTemplateId: testCaseTemplate.id as number,
        requestParameterValues: value.requestParameterValues,
        responseParameterValues: value.responseParameterValues
      };
      if (!!testCase?.id) {
        newTestCase.id = testCase.id;
        updateTestCase(newTestCase);
      } else {
        createTestCase(newTestCase);
      }
    })();
  }

  const onDeleteClick = (): void => {
    setShowDeleteMessage(true);
  }

  const closeDeleteMessageWindow = (isConfirm: boolean): void => {
    if (isConfirm && !!testCase?.id) {
      deleteTestCase(testCase.id);
    }
    setShowDeleteMessage(false);
  }

  return (
    <>
      <ModalWindow onClose={onCancel}
                   width={814}
                   title={!!testCase?.id ? t("testCase.edit") : `${t("testCase.add")} '${testCaseTemplate.name}'`}
                   children={
                     <>
                       <div className={styles.container}>
                         <div className={styles.inputContainer}>
                           <span>{t("common.name")}</span>
                           <div className={styles.input}>
                             <Input control={control}
                                    name={"name"}
                                    maxLength={100}
                                    error={!!errors.name ? errors.name.message : ""}/>
                           </div>
                         </div>
                         <div className={styles.title}>{t("testCase.request")}</div>
                         {requestParametersFields.map((field, index) => (
                           <TestCaseModalParameterInput
                             title={field.parameterName}
                             control={control}
                             error={!!errors.requestParameterValues?.[index]?.value ? errors.requestParameterValues?.[index]?.value?.message : ""}
                             name={`requestParameterValues.${index}.value`}
                             key={field.id}/>
                         ))}
                         <div className={styles.title}>{t("testCase.response")}</div>
                         {responseParametersFields.map((field, index) => (
                           <TestCaseModalParameterInput
                             title={field.parameterName}
                             isResponseParameters
                             control={control}
                             error={!!errors.responseParameterValues?.[index]?.value ? errors.responseParameterValues?.[index]?.value?.message : ""}
                             name={`responseParameterValues.${index}.value`}
                             key={field.id}/>
                         ))}
                       </div>
                       <div className={styles.buttonsContainer}>
                         <div className={styles.buttons}>
                           {!!testCase?.id && (
                             <button className={styles.delete}
                                     disabled={isLoadingCreate || isLoadingDelete || isLoadingUpdate}
                                     onClick={onDeleteClick}>{t("common.delete")}</button>
                           )}
                         </div>
                         <div className={styles.buttons}>
                           <button className={styles.cancel}
                                   disabled={isLoadingCreate || isLoadingDelete || isLoadingUpdate}
                                   onClick={onCancel}>{t("common.cancel")}</button>
                           <button className={styles.save}
                                   disabled={isLoadingCreate || isLoadingDelete || isLoadingUpdate}
                                   onClick={onClickSave}>{t("common.save")}</button>
                         </div>
                       </div>
                     </>
                   }
      />

      {showDeleteMessage && (
        <MessageWindow onClose={closeDeleteMessageWindow}
                       title={t("testCase.delete")}
                       description={!!testCase?.testRailTestCaseId
                         ? <Trans i18nKey="testCase.deleteDescriptionTestrail"/>
                         : t("testCase.deleteDescription")}
                       primaryText={t("common.ok")} secondaryText={t("common.cancel")}/>
      )}

      {showConfirmCancel && (
        <MessageWindow onClose={closeConfirmCancelModal}
                       title={t("common.cancelChanges")}
                       description={t("common.cancelChangesDescription")}
                       primaryText={t("common.ok")} secondaryText={t("common.cancel")}/>
      )}
    </>
  );
}